/**
 * Email layout helpers — shared HTML and text scaffolding
 * for all transactional templates. Provides responsive
 * wrapper, header with logo, footer with social/support.
 */

import type { BrandConfig } from './types';

export function emailShell(innerHtml: string, brand: BrandConfig): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>UNIVIBE</title>
</head>
<body style="margin:0;padding:0;background-color:#f8fafc;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f8fafc;padding:24px 0;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
          ${emailHeader(brand)}
          <tr>
            <td style="padding:32px 40px;">
              ${innerHtml}
            </td>
          </tr>
          ${emailFooter(brand)}
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

export function emailHeader(brand: BrandConfig): string {
  const logoHtml = brand.logoUrl
    ? `<img src="${brand.logoUrl}" alt="${brand.name}" style="height:36px;display:block;margin:0 auto;" />`
    : `<span style="font-size:20px;font-weight:700;color:${brand.primaryColor};">${brand.name}</span>`;
  return `<tr>
    <td style="padding:24px 40px;border-bottom:1px solid #e2e8f0;text-align:center;">
      ${logoHtml}
    </td>
  </tr>`;
}

export function emailFooter(brand: BrandConfig): string {
  const socialHtml = brand.socialLinks.length > 0
    ? `<tr><td style="padding:16px 40px 0;border-top:1px solid #e2e8f0;text-align:center;">
        <p style="margin:0 0 8px;font-size:13px;color:#64748b;">Follow us</p>
        ${brand.socialLinks.map((s) => `<a href="${s.url}" style="display:inline-block;margin:0 8px;color:${brand.primaryColor};text-decoration:none;font-size:13px;">${s.label}</a>`).join('')}
      </td></tr>`
    : '';
  return `<tr>
    <td style="padding:24px 40px;border-top:1px solid #e2e8f0;text-align:center;">
      <p style="margin:0 0 8px;font-size:13px;color:#64748b;">
        Need help? Contact us at
        <a href="mailto:${brand.supportEmail}" style="color:${brand.primaryColor};text-decoration:none;">${brand.supportEmail}</a>
      </p>
      <p style="margin:0;font-size:12px;color:#94a3b8;">
        &copy; ${new Date().getFullYear()} ${brand.name}. All rights reserved.
      </p>
    </td>
  </tr>
  ${socialHtml}`;
}

export function textFooter(brand: BrandConfig): string {
  const social = brand.socialLinks.length > 0
    ? '\nFollow us: ' + brand.socialLinks.map((s) => `${s.label} (${s.url})`).join(' | ')
    : '';
  return `\n---\nNeed help? Contact us at ${brand.supportEmail}\n© ${new Date().getFullYear()} ${brand.name}. All rights reserved.${social}`;
}

export function heading(text: string, color: string): string {
  return `<h1 style="margin:0 0 16px;font-size:24px;font-weight:700;color:${color};">${text}</h1>`;
}

export function paragraph(text: string): string {
  return `<p style="margin:0 0 16px;font-size:16px;line-height:1.6;color:#334155;">${text}</p>`;
}

export function infoBox(text: string, color: string): string {
  return `<div style="margin:0 0 24px;padding:16px 20px;background-color:${color}11;border-left:4px solid ${color};border-radius:4px;">
    <p style="margin:0;font-size:14px;line-height:1.5;color:#334155;">${text}</p>
  </div>`;
}
